import { Alignment, ObjectUtil, Collection, Color, getThemeKeys } from "@mcleod/core";
import { PropType } from "./PropType";
import { BorderType } from "./BorderType";
import { Cursor } from "./Cursor";
import { BlurEvent, ClickEvent, Component, DataDisplayEvent, DataSource, FocusEvent, KeyEvent, Layout } from "..";
import { StringOrPropsOrComponent } from "../page/getComponentFromStringOrPropsOrComponent";
import { DesignableObjectPropDefinition, DesignableObjectProps } from "./DesignableObjectProps";
import { DesignerInterface } from "./DesignerInterface";
import { MouseEvent } from "../events/MouseEvent";
import { HorizontalAlignment } from "@mcleod/core/src/constants/Alignment";
import { stringArrayEditor } from "../components/textbox/TextboxProps";

export type ComponentPropDefinitions = Collection<ComponentPropDefinition>;
export type ClickEventProp = string | ((event: ClickEvent) => void);
export type DataDisplayEventProp = string | ((event: DataDisplayEvent) => void);
export type DragEventProp = string | ((event: DragEvent) => void);
export type FocusEventProp = string | ((event: FocusEvent) => void);
export type BlurEventProp = string | ((event: BlurEvent) => void);
export type KeyEventProp = string | ((event: KeyEvent) => void);
export type TooltipCallback = (baseTooltip: Component, originatingEvent: MouseEvent) => Component|Promise<Component>;

export class ComponentPropDefinitionUtil {
  public static getComponentPropertyDefinitions(): ComponentPropDefinitions {
    return ObjectUtil.deepCopy(componentPropDefs);
  }

  public static getDataBoundPropertyDefinitions() {
    return ObjectUtil.deepCopy(dbProps);
  }

  public static populateComponentPropNames(props: ComponentPropDefinitions) {
    for (const key in props)
      props[key].name = key;
  }

  public static getDataSourceList(designerTab) {
    return Object.keys(designerTab.dataSources);
  }
}

export interface ComponentProps extends IdProps {
  _designer: DesignerInterface;
  _interactionEnabled: boolean;
  align: HorizontalAlignment;
  backgroundColor: Color;
  borderColor: Color;
  borderType: BorderType;
  borderShadow: boolean;
  borderWidth: number;
  borderBottomColor: Color;
  borderBottomType: BorderType;
  borderBottomWidth: number;
  borderLeftColor: Color;
  borderLeftType: BorderType;
  borderLeftWidth: number;
  borderRadius: string | number;
  borderBottomLeftRadius: string | number;
  borderBottomRightRadius: string | number;
  borderTopLeftRadius: string | number;
  borderTopRightRadius: string | number;
  borderRightColor: Color;
  borderRightType: BorderType;
  borderRightWidth: number;
  borderTopColor: Color;
  borderTopType: BorderType;
  borderTopWidth: number;
  bottom: string | number;
  boundRow: any; //should be a ModelRow, except the PropertiesTable still deals in plain old objects
  className: string;
  color: Color;
  cursor: Cursor;
  dataSource: DataSource;
  disabledTooltip: StringOrPropsOrComponent;
  displayLabel: string;
  draggable: boolean;
  enabled: boolean;
  excludeFromSortFields: string[];
  field: string;
  fillHeight: boolean;
  fillRow: boolean;
  fontBold: boolean;
  fontFamily: string;
  fontSize: string | number;
  height: string | number;
  isRow: boolean;
  left: string | number;
  license?: string;
  margin: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  minHeight: number | string;
  maxHeight: number | string;
  minWidth: number | string;
  maxWidth: number | string;
  onBlur?: BlurEventProp,
  onClick?: ClickEventProp,
  onDataDisplay?: DataDisplayEventProp,
  onDblClick?: ClickEventProp,
  onDragDrop?: DragEventProp,
  onDragEnd?: DragEventProp,
  onDragOver?: DragEventProp,
  onDragStart?: DragEventProp,
  onFocus?: FocusEventProp,
  onKeyDown?: KeyEventProp,
  onKeyUp?: KeyEventProp,
  owner?: Layout,
  padding: number;
  paddingTop: number;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
  rememberUserChoice: boolean;
  right: string | number;
  required: boolean;
  rowBreak: boolean;
  searchOnly: boolean;
  sortField: string;
  style: Partial<CSSStyleDeclaration>;
  themeKey: string;
  tooltip: StringOrPropsOrComponent;
  tooltipPosition: Alignment;
  top: string | number;
  visible: boolean;
  width: number | string;
  widthFillWeight: number;
  zIndex: number;
  preventCollapse: boolean;
}

export interface ComponentPropDefinition extends DesignableObjectPropDefinition {
  //nothing component-specific yet...
}

function getTextColorValues() {
  return getColorPropertyAttributes("color");
}

function getBackColorValues() {
  return getColorPropertyAttributes("backgroundColor");
}

export function getColorPropertyAttributes(colorProp: string) {
  const colors = getThemeKeys();
  const result = [];
  for (const color of colors)
    result.push({ caption: color, [colorProp]: color });
  return { items: result, itemResultField: colorProp };
}

const componentPropDefs: ComponentPropDefinitions = {
  align: { type: PropType.string, items: [HorizontalAlignment.LEFT, HorizontalAlignment.CENTER, HorizontalAlignment.RIGHT], defaultValue: HorizontalAlignment.LEFT, category: "Appearance", description: "This controls the horizontal alignment of component's content within it." },
  borderShadow: { type: PropType.bool, category: "Border", description: "This controls whether or not a shadowed border will appear behind the component.  Setting this affects the box-shadow CSS property." },
  border: { type: PropType.string, category: "Border", description: "This is a shorthand for setting all the properties of the component's border with one property." },
  borderWidth: { type: PropType.number, category: "Border", description: "This sets the width (in pixels) of the component's border.  By default, this is the only property that needs to be set for a component to have a solid border with the current color." },
  borderColor: { type: PropType.string, ...getTextColorValues(), category: "Border", description: "This sets the color of the component's border.  See the color demo page (provide link) for information about what values should be used.  This property will have no effect unless a borderWidth is also set." },
  borderType: { type: PropType.string, items: ["solid", "dashed", "dotted"], defaultValue: "solid", category: "Border", description: "This controls the type of border that will shown around a component.  This property will have no effect unless a borderWidth is also set." },
  borderRadius: { type: PropType.number, category: "Border", description: "Set this property to enable rounded borders.  It can be set to a number to specify the border radius in pixels or a string with a percent sign (e.g. 50%) to express the radius in terms of the component's size." },
  borderTop: { type: PropType.string, category: "Border" },
  borderTopWidth: { type: PropType.number, category: "Border" },
  borderTopColor: { type: PropType.string, ...getTextColorValues(), category: "Border" },
  borderTopType: { type: PropType.string, items: ["solid", "dashed", "dotted"], defaultValue: "solid", category: "Border" },
  borderTopLeftRadius: { type: PropType.number, category: "Border" },
  borderTopRightRadius: { type: PropType.number, category: "Border" },
  borderRight: { type: PropType.string, category: "Border" },
  borderRightWidth: { type: PropType.number, category: "Border" },
  borderRightColor: { type: PropType.string, ...getTextColorValues(), category: "Border" },
  borderRightType: { type: PropType.string, items: ["solid", "dashed", "dotted"], defaultValue: "solid", category: "Border" },
  borderBottom: { type: PropType.string, category: "Border" },
  borderBottomWidth: { type: PropType.number, category: "Border" },
  borderBottomColor: { type: PropType.string, ...getTextColorValues(), category: "Border" },
  borderBottomType: { type: PropType.string, items: ["solid", "dashed", "dotted"], defaultValue: "solid", category: "Border" },
  borderBottomLeftRadius: { type: PropType.number, category: "Border" },
  borderBottomRightRadius: { type: PropType.number, category: "Border" },
  borderLeft: { type: PropType.string, category: "Border" },
  borderLeftWidth: { type: PropType.number, category: "Border" },
  borderLeftColor: { type: PropType.string, ...getTextColorValues(), category: "Border" },
  borderLeftType: { type: PropType.string, items: ["solid", "dashed", "dotted"], defaultValue: "solid", category: "Border" },
  backgroundColor: { type: PropType.string, ...getBackColorValues(), category: "Appearance", description: "This sets which color fills the background of the component.  See the color demo page (provide link) for information about what values should be used." },
  color: { type: PropType.string, ...getTextColorValues(), category: "Appearance", description: "This sets which color will be used when rendering foreground elements (text, borders, images).  See the color demo page (provide link) for information about what values should be used." },
  cursor: { type: PropType.string, items: ["pointer"], category: "Uncommon", description: "This sets which mouse cursor will be shown when the mouse is over this component." },
  // className: { type: PropType.string },
  dataSource: { type: PropType.string, items: ComponentPropDefinitionUtil.getDataSourceList, category: "Data", source: "databound", description: "This specifies the DataSource that will provide this component with its data." },
  displayLabel: { type: PropType.string, description: "This defines the text that will be used to display this field in listings.  This is useful for giving the Component a description that is meaningful to the user for Components that do not have captions that the user already sees on the page." },
  draggable: { type: PropType.bool, category: "Uncommon", defaultValue: false, description: "This controls whether or not this component will fire drag events." },
  enabled: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This controls whether a user can interact with the component.  For example, a Button with enabled=false will not be clickable.  A Textbox with enabled=false will not be focusable." },
  enabledDuringAdd: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This controls whether a user can interact with the component when its DataSource is in ADD mode." },
  enabledDuringSearch: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This controls whether a user can interact with the component when its DataSource is in SEARCH mode." },
  enabledDuringUpdate: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This controls whether a user can interact with the component when its DataSource is in UPDATE mode." },
  excludeFromSortFields: { type: PropType.string, editor: stringArrayEditor, category: "Data", description: "List field names here (that are used bound to this component) that should not be avialable when sorting within table.  By default, all fields are eligible for use in table sorting." },
  field: { type: PropType.string, category: "Data", source: "databound", description: "This specifies which field from the DataSource that this component is bound to." },
  fillHeight: { type: PropType.bool, category: "Layout", description: "This toggles whether the component will fill its container's height." },
  fillRow: { type: PropType.bool, category: "Layout", description: "This toggles whether the component will fill its container's width.  If there are other fixed width components on the same row, this component will fill the container's width minus the fixed widths.  Multiple components on the same row can set fillRow=true and they will be given equal fill weight (1).  See widthFillWeight if you wish to make the components fill the row but not be equal widths." },
  fontBold: { type: PropType.bool, category: "Appearance", description: "This toggles whether or not the component's text will be bold (font-weight: 500)." },
  fontFamily: { type: PropType.string, category: "Appearance", description: "The sets the font family (e.g. courier, sans-serif) of the component's text." },
  fontSize: { type: PropType.string, items: ["xxsmall", "xsmall", "small", "medium", "large", "xlarge", "xxlarge", "xxxlarge", "skyline", "billboard"], category: "Appearance", description: "This sets the size of the text of the component.  It can be set to one of the predefined themable values or a number to indicate the font size in pixels." },
  height: { type: PropType.number, category: "Layout", description: "This specifies the a value for the height.  When no value is set, the component will be tall enough to fit its contents (plus any paddings and margins).  You can set height to a number for a height in pixels, or a string ending with a percent sign" },
  id: { type: PropType.string, description: "This specifies a unique identifier of a component within its layout.  This is used by permissions, among other things, to identify components by their qualified name.  It will also be used for the component's associated DOM element id attribute." },
  isRow: { type: PropType.bool, category: "Layout", description: "If set to true, this Component will be added to its container directly instead of creating a row for it." },
  left: { type: PropType.number, category: "Uncommon" },
  license: { type: PropType.string, category: "Enabled / visible", description: "When specified, the component will only be visible when the specified license is present." },
  width: { type: PropType.number, category: "Layout" },
  minHeight: { type: PropType.number, category: "Layout" },
  minWidth: { type: PropType.number, category: "Layout" },
  margin: { type: PropType.string, category: "Margin / padding" },
  marginTop: { type: PropType.number, category: "Margin / padding" },
  marginRight: { type: PropType.number, category: "Margin / padding" },
  marginBottom: { type: PropType.number, category: "Margin / padding" },
  marginLeft: { type: PropType.number, category: "Margin / padding" },
  onBlur: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addBlurListener",
    removeListenerMethod: "removeBlurListener",
    eventSignature: "OnBlur(event: BlurEvent)",
    description: "This event will fire when the component is the current focus of the page and then a different component becomes focused (i.e. the user tabbed to another field)."
  },
  onClick: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addClickListener",
    removeListenerMethod: "removeClickListener",
    eventSignature: "OnClick(event: ClickEvent)",
    description: "This event will fire when a click is enacted on the component."
  },
  onDataDisplay: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addDataDisplayListener",
    removeListenerMethod: "removeDataDisplayListener",
    eventSignature: "OnDataDisplay(event: DataDisplayEvent)",
    source: "databound",
    description: "This is an event that will be triggered when the DataSource that this component is bound to is displaying a new record."
  },
  onDblClick: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addDblClickListener",
    removeListenerMethod: "removeDblClickListener",
    eventSignature: "OnDblClick(event: ClickEvent)",
    description: "This event will fire when a double-click is enacted on the component."
  },
  onDragDrop: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addDropListener",
    removeListenerMethod: "removeDragDropListener",
    eventSignature: "OnDragDrop(event: DragEvent)",
    description: "This event will fire when the component is being dragged and is then dropped."
  },
  onDragEnd: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addDragEndListener",
    removeListenerMethod: "removeDragEndListener",
    eventSignature: "OnDragEnd(event: DragEvent)",
    description: "This event will fire when the component is being dragged and the drag action is completed."
  },
  onDragOver: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addDragOverListener",
    removeListenerMethod: "removeDragOverListener",
    eventSignature: "OnDragOver(event: DragEvent)",
    description: "This event will fire when the component is being dragged over a valid drop target."
  },
  onDragStart: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addDragStartListener",
    removeListenerMethod: "removeDragStartListener",
    eventSignature: "OnDragStart(event: DragEvent)",
    description: "This event will fire when the component first begins being dragged."
  },
  onFocus: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addFocusListener",
    removeListenerMethod: "removeFocusListener",
    eventSignature: "OnFocus(event: FocusEvent)",
    description: "This event will fire when the component becomes the current focus target of the page."
  },
  onKeyDown: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addKeyDownListener",
    removeListenerMethod: "removeKeyDownListener",
    eventSignature: "OnKeyDown(event: KeyEvent)",
    description: "This event will fire for the focused component when a key is pressed."
  },
  onKeyUp: {
    type: PropType.event,
    category: "Events",
    addListenerMethod: "addKeyUpListener",
    removeListenerMethod: "removeKeyUpListener",
    eventSignature: "OnKeyUp(event: KeyEvent)",
    description: "This event will fire for the focused component when a key is released."
  },
  padding: { type: PropType.number, category: "Margin / padding" },
  paddingTop: { type: PropType.number, category: "Margin / padding" },
  paddingRight: { type: PropType.number, category: "Margin / padding" },
  paddingBottom: { type: PropType.number, category: "Margin / padding" },
  paddingLeft: { type: PropType.number, category: "Margin / padding" },
  rememberUserChoice: { type: PropType.bool, category: "Data", description: "This controls whether or not this Component's value will be saved in local storage when changed and restored the next time the Component is loaded on a page." },
  required: { type: PropType.bool, category: "Data" },
  rowBreak: { type: PropType.bool, defaultValue: true, category: "Layout" },
  maxHeight: { type: PropType.number, category: "Layout" },
  maxWidth: { type: PropType.number, category: "Layout" },
  searchOnly: { type: PropType.bool, category: "Data", source: "databound", description: "This enables a component for searching a DataSource but not displaying the current values from the DataSource." },
  sortDescendingByDefault: { type: PropType.bool, category: "Data", description: "This controls whether or not the data for this Component should be sorted in descending order by default.  This is used when the Component is displayed in a Table and we don't want the user to have to sort twice to get the values to sort in descending order.  This is often used for date fields where it makes sense to show the most recent first, though there are other reasons to show this as well." },
  sortField: { type: PropType.string, category: "Data", description: "This specifies a field name which can be used during table sorting.  This property is intended to be used when a component display data from one or more fields, but doesn't specify its field property value.  The default value for this property will be the value in the field property." },
  sortNullsAtEnd: { type: PropType.bool, category: "Data", description: "This controls whether nulls (empty values) are put at the beginning or the end of the data when this Component is shown in a list." },
  widthFillWeight: { type: PropType.number, category: "Layout" },
  tooltip: { type: PropType.string },
  tooltipPosition: { type: PropType.string, defaultValue: Alignment.BOTTOM, items: [Alignment.LEFT, Alignment.RIGHT, Alignment.BOTTOM, Alignment.TOP], category: "Uncommon", description: "This specifies where the tooltip should appear relative to the component." },
  themeKey: { type: PropType.string, category: "Appearance", description: "Defines a key from the theme that will be applied to this component.  Properties from this theme element (usually colors) will be set in this component." },
  visible: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This specifies whether the component should be shown on the page." },
  visibleDuringAdd: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This specifies whether the component should be shown on the page when its DataSource is in ADD mode." },
  visibleDuringSearch: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This specifies whether the component should be shown on the page when its DataSource is in UPDATE mode." },
  visibleDuringUpdate: { type: PropType.bool, defaultValue: true, category: "Enabled / visible", description: "This specifies whether the component should be shown on the page when its DataSource is in SEARCH mode." },
  preventCollapse: { type: PropType.bool, defaultValue: false, category: "Enabled / visible", description: "When checked, this component will not collapse when its visibility is false." },
  zIndex: { type: PropType.number, category: "Uncommon" },
};

for (const key in componentPropDefs) {
  const prop = componentPropDefs[key];
  prop.name = key;
  prop.source = "component";
}

export interface IdProps extends DesignableObjectProps {
  id?: string;
}

const dbProps: ComponentPropDefinitions = {
  dataSource: { type: PropType.string, items: ComponentPropDefinitionUtil.getDataSourceList, category: "Data", source: "databound", description: "This specifies the DataSource that will provide this component with its data." },
  field: { type: PropType.string, category: "Data", source: "databound", description: "This specifies which field from the DataSource that this component is bound to." },
  searchOnly: { type: PropType.bool, category: "Data", source: "databound", description: "This enables a component for searching a DataSource but not displaying the current values from the DataSource." },
};
